import type { ApiEnvelope } from './types';

/**
 * 防御性 fetch 客户端：
 *  - 自动拼接 /api/v1 前缀
 *  - 自动注入 Bearer 令牌（由 auth store 提供）
 *  - 强制校验 response.ok 与 content-type，避免把 HTML 错误页当 JSON 解析
 *  - 统一抛出 ApiError，包含 status / 后端 code+message，便于上层友好展示
 *
 * 红线：不在错误路径上塞假数据；任何非预期响应都抛错并交由 UI 渲染 error 状态。
 */

export class ApiError extends Error {
  status: number;
  /**
   * Issue #45（P1-3）：稳定机器码。兼容两种后端错误体：ApiEnvelope 的 **数字** 业务码，以及 Nest
   * 异常 `{ statusCode, code: string, message }` 的**稳定字符串**机器码（如 `SMS_SEND_FAILED`、
   * `AUTH_RATE_LIMITED`、`CHALLENGE_EXPIRED`、`RECOVERY_BOOTSTRAP_ORIGIN_FORBIDDEN`）。绝不因
   * 「code 非 number」而丢码退化为通用 HTTP 错误。
   */
  code?: string | number;
  payload?: unknown;
  constructor(
    message: string,
    status: number,
    code?: string | number,
    payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
  /**
   * Issue #322：当前 refresh token 读取器（body 令牌模式）。Issue #401 起管理端
   * 改走 HttpOnly Cookie 续期，不再提供本读取器——doRefresh 凭 Cookie 调用
   * /auth/refresh。两种模式都由 onRefreshed 是否提供决定 401 是否自动刷新。
   */
  getRefreshToken?: () => string | null;
  /**
   * Issue #322：刷新成功后回调，写入新 access token。Issue #401：Cookie 模式下
   * 响应体不含 refreshToken（轮换发生在 HttpOnly Cookie 内），故该字段可选。
   */
  onRefreshed?: (tokens: { accessToken: string; refreshToken?: string }) => void;
}

const DEFAULT_BASE = '/api/v1';

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized?: () => void;
  private readonly fetchImpl: typeof fetch;
  private readonly getRefreshToken?: () => string | null;
  private readonly onRefreshed?: (tokens: {
    accessToken: string;
    refreshToken?: string;
  }) => void;
  /**
   * Issue #322：单飞刷新句柄。并发 401 共享同一刷新 promise，避免多个请求同时刷新导致
   * refresh token 轮换互相失效（旧 token 被撤销后其它并发刷新会失败）。
   */
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? DEFAULT_BASE;
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.getRefreshToken = opts.getRefreshToken;
    this.onRefreshed = opts.onRefreshed;
  }

  /**
   * Issue #322：是否为认证端点（登录 / 刷新 / 登出）。这些端点的 401 绝不触发自动刷新——
   * 否则刷新失败会无限递归刷新；登录失败也不应清理已有会话。
   */
  private isAuthEndpoint(path: string): boolean {
    // Issue #21：短信二次验证 / 恢复码端点同属认证入口——其 401（账号不可用 / 凭据无效）
    // 绝不触发自动刷新，否则会把「验证码 / 恢复码错误」误当会话过期、清会话跳登录。
    // Issue #38（P1）：recovery-bootstrap 也纳入——它要接收 HttpOnly bootstrap Set-Cookie
    // （credentials: 'include'），且其 403（Origin 不符）绝不触发自动刷新。
    return /^\/?auth\/(login|refresh|logout|verify-sms|resend-sms|recovery-bootstrap|recovery-login)\b/.test(
      path,
    );
  }

  /**
   * Issue #322：单飞刷新 access token。并发 401 复用同一 promise；刷新结束后清空句柄，
   * 后续 401 再发起新的刷新。返回是否刷新成功。
   */
  private ensureRefreshed(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.doRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  /**
   * Issue #322：调用 /auth/refresh 续期。后端校验账户状态 / deletedAt / tokenVersion / 过期等
   * 既有约束；成功则把新 access token 写回 store（onRefreshed），失败返回 false。绝不打印 token 明文。
   *
   * Issue #401：管理端（Web）无本地 refresh token——请求体为空，凭 HttpOnly Cookie
   * 续期（credentials: 'include'），轮换后的新 refresh token 由后端写回 Cookie，
   * 响应体只回传 accessToken。提供 getRefreshToken 的旧 body 模式保持兼容。
   */
  private async doRefresh(): Promise<boolean> {
    if (!this.onRefreshed) return false;
    const refreshToken = this.getRefreshToken?.() ?? null;
    let res: Response;
    try {
      res = await this.fetchImpl(this.buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...(refreshToken ? { 'Content-Type': 'application/json' } : {}),
          Accept: 'application/json',
        },
        body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
      });
    } catch {
      // 网络层异常（断网 / 超时 / CORS）：视为刷新失败，交由调用方 onUnauthorized，
      // 不抛错以免打断重放语义。
      return false;
    }
    // 401：refresh token 确已失效（过期 / 撤销 / tokenVersion 变更）——保持既有语义：
    // 返回 false 让调用方清会话跳登录，绝不重试。放在 content-type 校验之前，
    // 确保这条「会话失效」红线不被基础设施返回的响应体形态干扰。
    if (res.status === 401) return false;
    // content-type 防御：网关 / 反代可能返回 text/html 等非 JSON（如 502 错误页、
    // 200 伪成功页）。绝不静默吞成 refresh=false，否则会掩盖基础设施故障——
    // 抛出带完整 HTTP status 的 ApiError，交由上层暴露真实错误。
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      const text = await res.text().catch(() => '');
      throw new ApiError(
        `Unexpected non-JSON refresh response (status=${res.status})`,
        res.status,
        undefined,
        text.slice(0, 200),
      );
    }
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new ApiError(
        `Failed to parse JSON refresh response (status=${res.status})`,
        res.status,
      );
    }
    // 其它非 2xx 但为 JSON 的响应（如后端 500 错误信封）：保持既有语义返回 false。
    if (!res.ok) return false;
    const data = isEnvelope(body) ? body.data : body;
    const rec = data as { accessToken?: unknown; refreshToken?: unknown } | null;
    if (!rec || typeof rec.accessToken !== 'string') {
      return false;
    }
    this.onRefreshed({
      accessToken: rec.accessToken,
      refreshToken:
        typeof rec.refreshToken === 'string' ? rec.refreshToken : undefined,
    });
    return true;
  }

  async request<T>(
    path: string,
    init: RequestInit & { query?: Record<string, unknown> } = {},
    isRetry = false,
  ): Promise<T> {
    const { query, headers, ...rest } = init;
    const url = this.buildUrl(path, query);
    const finalHeaders = new Headers(headers ?? {});
    if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json');
    // multipart（FormData）请求绝不显式设 Content-Type：浏览器需自动注入带 boundary 的
    // multipart/form-data，否则后端无法解析分块（Issue #316 课程音频上传）。
    const isFormData =
      typeof FormData !== 'undefined' && rest.body instanceof FormData;
    if (rest.body && !isFormData && !finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
    const token = this.getToken();
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);

    let response: Response;
    try {
      // Issue #401：认证端点显式携带凭据——登录要接收 Set-Cookie（HttpOnly refresh
      // cookie），登出/刷新要回传该 Cookie。业务端点保持默认（同源本就带 Cookie，
      // 且 refresh cookie 的 path 限定在 /auth 下，不会随业务请求外溢）。
      const credentials: RequestInit['credentials'] | undefined =
        this.isAuthEndpoint(path) ? 'include' : rest.credentials;
      response = await this.fetchImpl(url, {
        ...rest,
        ...(credentials ? { credentials } : {}),
        headers: finalHeaders,
      });
    } catch (err) {
      throw new ApiError(
        err instanceof Error ? err.message : 'Network error',
        0,
      );
    }

    if (response.status === 401) {
      // Issue #322：access token 过期 → 单飞刷新 + 重放原请求；刷新失败才清会话跳登录。
      // auth 端点（登录/刷新/登出）的 401 不触发刷新、不清会话——交由调用方（登录表单等）处理，
      // 避免递归刷新与「登录失败误清会话」。
      if (this.isAuthEndpoint(path)) {
        // fall through to throw the ApiError below
      } else if (!isRetry) {
        const refreshed = await this.ensureRefreshed();
        if (refreshed) {
          // 重放一次：会读到 store 里刚写入的新 access token。
          return this.request<T>(path, init, true);
        }
        // 刷新失败（无 refresh token / 后端拒绝）→ 会话确已失效。
        this.onUnauthorized?.();
      } else {
        // 刷新后重放仍 401：会话确已失效。
        this.onUnauthorized?.();
      }
    }

    const contentType = response.headers.get('content-type') ?? '';

    if (response.status === 204) {
      return undefined as T;
    }

    if (!contentType.includes('application/json')) {
      const text = await response.text().catch(() => '');
      throw new ApiError(
        `Unexpected non-JSON response (status=${response.status})`,
        response.status,
        undefined,
        text.slice(0, 200),
      );
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new ApiError(
        `Failed to parse JSON response (status=${response.status})`,
        response.status,
      );
    }

    if (!response.ok) {
      // Issue #45（P1-3）：后端错误体两种形态都要保留**稳定机器码 + 可操作消息**，绝不退化为
      // 通用 HTTP 错误：ApiEnvelope `{ data, code:number, message }` 与 Nest 异常
      // `{ statusCode, code:string, message }`（携带字符串机器码）。
      const { code, message } = extractErrorFields(body);
      throw new ApiError(
        message ?? `Request failed with status ${response.status}`,
        response.status,
        code,
        body,
      );
    }

    if (isEnvelope(body)) {
      if (typeof body.code === 'number' && body.code !== 0) {
        throw new ApiError(
          body.message ?? 'Backend returned non-zero code',
          response.status,
          body.code,
          body,
        );
      }
      return body.data as T;
    }
    return body as T;
  }

  get<T>(path: string, query?: Record<string, unknown>) {
    return this.request<T>(path, { method: 'GET', query });
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'PUT',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  delete<T>(path: string, body?: unknown) {
    // Issue #235：删除请求可携带图形验证码（captchaId + captchaCode）于 body，由后端校验。
    return this.request<T>(path, {
      method: 'DELETE',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  /**
   * Issue #316：multipart 文件上传（如课程音频）。直接传 FormData，不做 JSON 序列化，
   * 也不显式设 Content-Type（由浏览器注入带 boundary 的 multipart/form-data）。错误处理
   * 与其它请求一致：非 JSON / 413 / 415 / 403 / 非零 code 都抛出携带后端中文 message 的 ApiError。
   */
  upload<T>(path: string, formData: FormData) {
    return this.request<T>(path, { method: 'POST', body: formData });
  }

  /**
   * Issue #7 / #8：文件下载（课程 Markdown 导出、通话 CSV 导出）。
   *
   * 为什么不能走 `request()`：`request()` 会对**任何非 JSON 响应**直接抛
   * `Unexpected non-JSON response`（这是它有意的防线：生产上常见的失败不是业务 JSON，
   * 而是 Nginx HTML 错误页 / 登录页）。文件下载的成功响应恰恰不是 JSON，所以需要一条
   * 独立通道——但**不能因此放弃校验**：
   *
   *   - 复用 buildUrl / Bearer 注入 / 401 单飞刷新 + 重放（与业务请求同一套会话语义）；
   *   - **强制校验 HTTP 状态**：非 2xx 一律抛 ApiError；若错误体是 JSON 信封，则把后端
   *     中文 message 提出来（后端的 403/404/413 都是 JSON 错误体）；
   *   - **强制校验 content-type**：即便 200，只要不是期望的文件类型（比如被 Nginx 反代
   *     成了 HTML 登录页），也一律按失败抛错，绝不把 HTML 当成 .csv 存到用户盘上；
   *   - 文件名以服务端 `Content-Disposition` 为准（RFC 5987 `filename*` 承载中文）。
   */
  async download(
    path: string,
    options: {
      query?: Record<string, unknown>;
      /** 期望的 content-type 前缀（如 'text/csv'）。不匹配即视为失败。 */
      expectContentType: string;
      /** 服务端未给出 Content-Disposition 时的兜底文件名。 */
      fallbackFileName: string;
    },
    isRetry = false,
  ): Promise<{ blob: Blob; fileName: string }> {
    const url = this.buildUrl(path, options.query);
    const headers = new Headers({ Accept: options.expectContentType });
    const token = this.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let response: Response;
    try {
      response = await this.fetchImpl(url, { method: 'GET', headers });
    } catch (err) {
      throw new ApiError(err instanceof Error ? err.message : 'Network error', 0);
    }

    if (response.status === 401) {
      if (!isRetry) {
        const refreshed = await this.ensureRefreshed();
        if (refreshed) {
          return this.download(path, options, true);
        }
        this.onUnauthorized?.();
      } else {
        this.onUnauthorized?.();
      }
    }

    const contentType = response.headers.get('content-type') ?? '';

    if (!response.ok) {
      // 后端的 403 / 404 / 413 都是 JSON 错误体：把中文 message 提出来给 UI 展示。
      let message = `导出失败（HTTP ${response.status}）`;
      let code: string | number | undefined;
      if (contentType.includes('application/json')) {
        const body: unknown = await response.json().catch(() => null);
        const extracted = extractErrorFields(body);
        if (extracted.message) message = extracted.message;
        code = extracted.code;
      }
      throw new ApiError(message, response.status, code);
    }

    // 200 但不是期望的文件类型 → 多半是被反代拦成了 HTML 登录页 / 错误页。
    if (!contentType.includes(options.expectContentType)) {
      throw new ApiError(
        `导出失败：服务端返回了非预期的内容类型（${contentType || '未知'}）`,
        response.status,
      );
    }

    const blob = await response.blob();
    const fileName =
      parseContentDispositionFileName(response.headers.get('content-disposition')) ??
      options.fallbackFileName;
    return { blob, fileName };
  }

  private buildUrl(path: string, query?: Record<string, unknown>): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const queryStr = encodeQuery(query);
    return `${this.baseUrl}${cleanPath}${queryStr}`;
  }
}

function isEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in (value as Record<string, unknown>) &&
    'code' in (value as Record<string, unknown>)
  );
}

/**
 * Issue #45（P1-3）：从后端错误体提取**稳定机器码 + 可操作消息**，兼容两种形态：
 *   1) ApiEnvelope `{ data, code:number, message }`（业务信封）；
 *   2) Nest 异常 `{ statusCode, code:string, message }`（携带稳定字符串机器码，如 `SMS_SEND_FAILED`）。
 * 关键：Nest 异常体**没有** `data` 字段（isEnvelope 为 false）、`code` 为**字符串**——旧实现因此
 * 丢掉 code/message 退化为「Request failed with status N」的通用错误。此处按字段直取，两种形态通吃。
 */
function extractErrorFields(body: unknown): {
  code?: string | number;
  message?: string;
} {
  if (typeof body !== 'object' || body === null) return {};
  const rec = body as Record<string, unknown>;
  const code =
    typeof rec.code === 'string' || typeof rec.code === 'number'
      ? rec.code
      : undefined;
  const message =
    typeof rec.message === 'string' && rec.message ? rec.message : undefined;
  return { code, message };
}

/**
 * Issue #7 / #8：从 `Content-Disposition` 解析下载文件名。
 *
 * 优先取 RFC 5987 的 `filename*=UTF-8''<percent-encoded>`（中文名走这里；`filename=` 只是
 * 纯 ASCII 兜底，中文会被删空）。解析失败返回 null，由调用方回退到本地兜底名。
 */
export function parseContentDispositionFileName(header: string | null): string | null {
  if (!header) return null;

  // filename*=UTF-8''%E6%B8%A0%E9%81%93.md
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch {
      // 编码损坏 → 落到下面的 ASCII filename=。
    }
  }

  // filename="export.md"
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  return bare?.[1]?.trim() || null;
}

export function encodeQuery(query?: Record<string, unknown>): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, raw] of Object.entries(query)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (Array.isArray(raw)) {
      raw.forEach((v) => params.append(key, String(v)));
    } else {
      params.set(key, String(raw));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}
